import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listSuppliers, upsertSupplier, deleteSupplier } from "@/lib/suppliers.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { ExportMenu } from "@/components/ui/export-menu";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Pencil, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/suppliers")({ component: SuppliersPage });

type Row = {
  id: string; name: string; code: string | null;
  contact_name: string | null; contact_email: string | null; contact_phone: string | null;
  website: string | null; default_lead_time_days: number; default_currency: string;
  payment_terms: string | null; notes: string | null; active: boolean;
  linked_policies: number; pending_suggestions: number;
};

function SuppliersPage() {
  const { tenantId } = useScope();
  const listFn = useServerFn(listSuppliers);
  const delFn = useServerFn(deleteSupplier);
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Row | null>(null);
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["suppliers", tenantId],
    enabled: !!tenantId,
    queryFn: () => listFn({ data: { tenantId: tenantId!, includeInactive: true } }),
  });

  const del = useMutation({
    mutationFn: (id: string) => delFn({ data: { id, tenantId: tenantId! } }),
    onSuccess: () => { toast.success("Supplier deleted"); qc.invalidateQueries({ queryKey: ["suppliers"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const rows = (data?.rows ?? []) as Row[];

  return (
    <>
      <PageHeader
        title="Suppliers"
        description="Vendors and manufacturers used in reorder policies and purchase orders."
        actions={
          <div className="flex items-center gap-2">
            {tenantId ? (
              <ExportMenu
                entity="suppliers"
                tenantId={tenantId}
                getRows={() => rows.map((r) => ({
                  name: r.name,
                  code: (r as any).code ?? "",
                  contact_email: (r as any).contact_email ?? "",
                  contact_phone: (r as any).contact_phone ?? "",
                  website: (r as any).website ?? "",
                  lead_time_days: (r as any).lead_time_days ?? "",
                  payment_terms: (r as any).payment_terms ?? "",
                  active: (r as any).active ?? true,
                }))}
                columns={["name","code","contact_email","contact_phone","website","lead_time_days","payment_terms","active"]}
                disabled={rows.length === 0}
              />
            ) : null}
            <Button size="sm" onClick={() => { setEditing(null); setOpen(true); }}>
              <Plus className="h-4 w-4 mr-1" /> New supplier
            </Button>
          </div>
        }
      />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead className="text-right">Lead time</TableHead>
                  <TableHead>Terms</TableHead>
                  <TableHead className="text-right">Policies</TableHead>
                  <TableHead className="text-right">Pending POs</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[120px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">No suppliers yet. Add your first vendor to start linking reorder policies.</TableCell></TableRow>
                ) : rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">
                      {r.name}
                      {r.website && (
                        <a href={r.website} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center text-muted-foreground hover:text-foreground">
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{r.code ?? "—"}</TableCell>
                    <TableCell className="text-sm">
                      {r.contact_name && <div>{r.contact_name}</div>}
                      {r.contact_email && <div className="text-muted-foreground">{r.contact_email}</div>}
                      {r.contact_phone && <div className="text-muted-foreground">{r.contact_phone}</div>}
                      {!r.contact_name && !r.contact_email && !r.contact_phone && <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{r.default_lead_time_days}d</TableCell>
                    <TableCell className="text-muted-foreground">{r.payment_terms ?? "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.linked_policies}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.pending_suggestions}</TableCell>
                    <TableCell>
                      <Badge variant={r.active ? "default" : "secondary"}>{r.active ? "Active" : "Inactive"}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => { setEditing(r); setOpen(true); }}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => { if (confirm(`Delete supplier "${r.name}"?`)) del.mutate(r.id); }}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
      <SupplierDialog
        open={open}
        onOpenChange={setOpen}
        existing={editing}
        tenantId={tenantId}
        onSaved={() => { setOpen(false); qc.invalidateQueries({ queryKey: ["suppliers"] }); }}
      />
    </>
  );
}

function SupplierDialog({
  open, onOpenChange, existing, tenantId, onSaved,
}: {
  open: boolean; onOpenChange: (v: boolean) => void; existing: Row | null;
  tenantId: string | null | undefined; onSaved: () => void;
}) {
  const upsertFn = useServerFn(upsertSupplier);
  const [form, setForm] = useState(() => initForm(existing));

  // reset when opening or switching record
  const key = (existing?.id ?? "new") + ":" + String(open);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useStateReset(key, () => setForm(initForm(existing)));

  const save = useMutation({
    mutationFn: () => upsertFn({ data: { ...form, tenantId: tenantId!, id: existing?.id } }),
    onSuccess: () => { toast.success(existing ? "Supplier updated" : "Supplier created"); onSaved(); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{existing ? "Edit supplier" : "New supplier"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label>Code</Label>
            <Input value={form.code ?? ""} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          </div>
          <div>
            <Label>Currency</Label>
            <Input value={form.default_currency} maxLength={3} onChange={(e) => setForm({ ...form, default_currency: e.target.value.toUpperCase() })} />
          </div>
          <div>
            <Label>Default lead time (days)</Label>
            <Input type="number" min={0} value={form.default_lead_time_days}
              onChange={(e) => setForm({ ...form, default_lead_time_days: Number(e.target.value || 0) })} />
          </div>
          <div>
            <Label>Payment terms</Label>
            <Input placeholder="Net 30" value={form.payment_terms ?? ""} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} />
          </div>
          <div>
            <Label>Contact name</Label>
            <Input value={form.contact_name ?? ""} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
          </div>
          <div>
            <Label>Contact email</Label>
            <Input type="email" value={form.contact_email ?? ""} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
          </div>
          <div>
            <Label>Contact phone</Label>
            <Input value={form.contact_phone ?? ""} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
          </div>
          <div>
            <Label>Website</Label>
            <Input placeholder="https://" value={form.website ?? ""} onChange={(e) => setForm({ ...form, website: e.target.value })} />
          </div>
          <div className="col-span-2">
            <Label>Notes</Label>
            <Textarea rows={3} value={form.notes ?? ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="col-span-2 flex items-center gap-2">
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            <Label className="!m-0">Active</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={!form.name.trim() || save.isPending}>
            {save.isPending ? "Saving…" : existing ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function initForm(r: Row | null) {
  return {
    name: r?.name ?? "",
    code: r?.code ?? "",
    contact_name: r?.contact_name ?? "",
    contact_email: r?.contact_email ?? "",
    contact_phone: r?.contact_phone ?? "",
    website: r?.website ?? "",
    default_lead_time_days: r?.default_lead_time_days ?? 14,
    default_currency: r?.default_currency ?? "USD",
    payment_terms: r?.payment_terms ?? "",
    notes: r?.notes ?? "",
    active: r?.active ?? true,
  };
}

// Tiny helper to reset form state when dialog open/record changes
import { useEffect, useRef } from "react";
function useStateReset(key: string, fn: () => void) {
  const last = useRef(key);
  useEffect(() => {
    if (last.current !== key) { last.current = key; fn(); }
  }, [key, fn]);
}